import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Edit, Trash2, DollarSign, TrendingDown, Download, FileText } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [reportForm, setReportForm] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    selected_clients: []
  });
  const [expenseForm, setExpenseForm] = useState({
    vendor_name: '',
    expense_type: 'office',
    amount: '',
    expense_method: 'cash',
    expense_date: new Date().toISOString().split('T')[0],
    gst_amount: '',
    description: '',
    notes: ''
  });
  const navigate = useNavigate();

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`);
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  useEffect(() => {
    fetchExpenses();
    fetchClients();
  }, []);

  // Record new expense
  const handleRecordExpense = async (e) => {
    e.preventDefault();
    
    if (editingExpense) {
      return handleUpdateExpense(e);
    }
    
    try {
      const expenseData = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        gst_amount: expenseForm.gst_amount ? parseFloat(expenseForm.gst_amount) : 0
      };
      
      await axios.post(`${API}/expenses`, expenseData);
      
      resetForm();
      fetchExpenses();
      alert('Expense recorded successfully!');
      
    } catch (error) {
      console.error('Error recording expense:', error);
      alert('Error recording expense');
    }
  };

  // Update expense
  const handleUpdateExpense = async (e) => {
    e.preventDefault();
    
    try {
      const expenseData = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        gst_amount: expenseForm.gst_amount ? parseFloat(expenseForm.gst_amount) : 0
      };
      
      await axios.put(`${API}/expenses/${editingExpense.id}`, expenseData);
      
      resetForm();
      fetchExpenses();
      alert('Expense updated successfully!');
      
    } catch (error) {
      console.error('Error updating expense:', error);
      alert('Error updating expense');
    }
  };

  // Edit expense
  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      vendor_name: expense.vendor_name || '',
      expense_type: expense.expense_type,
      amount: expense.amount.toString(),
      expense_method: expense.expense_method || 'cash',
      expense_date: expense.expense_date,
      gst_amount: expense.gst_amount ? expense.gst_amount.toString() : '',
      description: expense.description || '',
      notes: expense.notes || ''
    });
    
    setShowExpenseModal(true);
  };

  // Delete expense
  const handleDeleteExpense = async (expenseId) => {
    if (!confirm('Are you sure you want to delete this expense? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      fetchExpenses();
      alert('Expense deleted successfully!');
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Error deleting expense');
    }
  };

  // Reset form and close modal
  const resetForm = () => {
    setExpenseForm({
      vendor_name: '',
      expense_type: 'office',
      amount: '',
      expense_method: 'cash',
      expense_date: new Date().toISOString().split('T')[0],
      gst_amount: '',
      description: '',
      notes: ''
    });
    setEditingExpense(null);
    setShowExpenseModal(false);
  };

  // Export expenses report
  const handleExportReport = async () => {
    try {
      const params = new URLSearchParams({
        start_date: reportForm.start_date,
        end_date: reportForm.end_date
      });
      
      const response = await axios.get(`${API}/expenses/reports/export?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Expense_Report_${reportForm.start_date}_to_${reportForm.end_date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setShowReportsModal(false);
      alert('Report exported successfully!');
      
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Error exporting report');
    }
  };

  const getExpenseTypeBadge = (type) => {
    const types = {
      office: { label: 'Office Expense', color: 'bg-blue-500' },
      travel: { label: 'Travel', color: 'bg-green-500' },
      meals: { label: 'Meals & Entertainment', color: 'bg-orange-500' },
      professional: { label: 'Professional Services', color: 'bg-purple-500' },
      tax: { label: 'Tax Payment', color: 'bg-red-500' },
      utilities: { label: 'Utilities', color: 'bg-teal-500' },
      supplies: { label: 'Supplies', color: 'bg-indigo-500' },
      other: { label: 'Other', color: 'bg-gray-500' }
    };
    
    const expenseType = types[type] || types.other;
    
    return (
      <Badge className={`${expenseType.color} text-white`}>
        {expenseType.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <CardTitle className="text-3xl font-bold text-gray-800 flex items-center justify-center">
            <TrendingDown className="w-8 h-8 mr-3 text-red-600" />
            Expenses & Payments
          </CardTitle>
          <CardDescription className="text-gray-600 text-lg">
            Track and manage business expenses and tax payments
          </CardDescription>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">{expenses.filter(e => e.expense_type === 'office').length}</div>
              <div className="text-blue-100">Office Expenses</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">{expenses.filter(e => e.expense_type === 'tax').length}</div>
              <div className="text-red-100">Tax Payments</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">{expenses.filter(e => e.expense_type === 'travel').length}</div>
              <div className="text-green-100">Travel Expenses</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold mb-2">₹{expenses.reduce((sum, e) => sum + (e.amount || 0), 0).toLocaleString()}</div>
              <div className="text-purple-100">Total Expenses</div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <Card className="bg-white/80 backdrop-blur-sm border-orange-200 shadow-xl">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent mb-2">
                  Expense Tracking
                </h1>
                <p className="text-gray-600 text-lg">Monitor business expenses and payments with comprehensive tracking</p>
              </div>
              
              <div className="mt-4 md:mt-0 flex items-center space-x-4">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-2xl shadow-lg">
                  <div className="text-sm font-medium">Total Expenses</div>
                  <div className="text-2xl font-bold">{expenses.length}</div>
                </div>
                <Button 
                  onClick={() => setShowReportsModal(true)}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Export Reports
                </Button>
                <Button 
                  data-testid="add-expense-btn"
                  onClick={() => {
                    setEditingExpense(null);
                    setShowExpenseModal(true);
                  }}
                  className="bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Record Expense
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expenses Table */}
        <Card className="bg-white/80 backdrop-blur-sm border-orange-200 shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl font-bold flex items-center">
              <TrendingDown className="w-6 h-6 mr-3" />
              Expense Records
            </CardTitle>
            <CardDescription>
              Comprehensive tracking of all business expenses and payments made
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-orange-100">
                    <TableHead className="font-semibold text-gray-700">Date</TableHead>
                    <TableHead className="font-semibold text-gray-700">Vendor/Description</TableHead>
                    <TableHead className="font-semibold text-gray-700">Type</TableHead>
                    <TableHead className="font-semibold text-gray-700">Amount</TableHead>
                    <TableHead className="font-semibold text-gray-700">GST</TableHead>
                    <TableHead className="font-semibold text-gray-700">Method</TableHead>
                    <TableHead className="font-semibold text-gray-700">Notes</TableHead>
                    <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                
                <TableBody>
                  {expenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell>{new Date(expense.expense_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{expense.vendor_name || 'N/A'}</div>
                          {expense.description && (
                            <div className="text-sm text-gray-500">{expense.description}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getExpenseTypeBadge(expense.expense_type)}</TableCell>
                      <TableCell className="font-medium">₹{expense.amount.toLocaleString()}</TableCell>
                      <TableCell>
                        {expense.gst_amount ? (
                          <span className="text-green-600 font-medium">₹{expense.gst_amount.toLocaleString()}</span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {expense.expense_method ? expense.expense_method.replace('_', ' ').toUpperCase() : 'CASH'}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{expense.notes || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            onClick={() => handleEditExpense(expense)}
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteExpense(expense.id)}
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {expenses.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <TrendingDown className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium">No expenses recorded yet</p>
                <p>Start by recording your first business expense</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Expense Modal */}
      {showExpenseModal && (
        <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-gray-800 flex items-center">
                {editingExpense ? (
                  <>
                    <Edit className="w-5 h-5 mr-2 text-red-600" />
                    Edit Expense
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2 text-red-600" />
                    Record Expense
                  </>
                )}
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                {editingExpense ? 'Update expense details' : 'Record a new business expense'}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleRecordExpense} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="vendor_name">Vendor/Payee Name *</Label>
                  <Input
                    id="vendor_name"
                    type="text"
                    value={expenseForm.vendor_name}
                    onChange={(e) => setExpenseForm({...expenseForm, vendor_name: e.target.value})}
                    placeholder="Enter vendor or payee name"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="expense_type">Expense Type *</Label>
                  <Select 
                    value={expenseForm.expense_type} 
                    onValueChange={(value) => setExpenseForm({...expenseForm, expense_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="office">Office Expense</SelectItem>
                      <SelectItem value="travel">Travel</SelectItem>
                      <SelectItem value="meals">Meals & Entertainment</SelectItem>
                      <SelectItem value="professional">Professional Services</SelectItem>
                      <SelectItem value="tax">Tax Payment</SelectItem>
                      <SelectItem value="utilities">Utilities</SelectItem>
                      <SelectItem value="supplies">Supplies</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="amount">Amount *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                    placeholder="Enter expense amount"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="gst_amount">GST Amount (if applicable)</Label>
                  <Input
                    id="gst_amount"
                    type="number"
                    step="0.01"
                    value={expenseForm.gst_amount}
                    onChange={(e) => setExpenseForm({...expenseForm, gst_amount: e.target.value})}
                    placeholder="Enter GST amount"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="expense_method">Payment Method *</Label>
                  <Select 
                    value={expenseForm.expense_method} 
                    onValueChange={(value) => setExpenseForm({...expenseForm, expense_method: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="expense_date">Expense Date *</Label>
                  <Input
                    id="expense_date"
                    type="date"
                    value={expenseForm.expense_date}
                    onChange={(e) => setExpenseForm({...expenseForm, expense_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  type="text"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                  placeholder="Brief description of the expense"
                />
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  type="text"
                  value={expenseForm.notes}
                  onChange={(e) => setExpenseForm({...expenseForm, notes: e.target.value})}
                  placeholder="Additional notes or remarks"
                />
              </div>

              {/* Expense Summary */}
              {expenseForm.amount && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <h4 className="font-medium text-red-800 mb-2">Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Amount:</span>
                      <span className="font-medium">₹{parseFloat(expenseForm.amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>GST:</span>
                      <span className="font-medium">₹{parseFloat(expenseForm.gst_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-1">
                      <span>Total:</span>
                      <span className="font-medium">₹{(parseFloat(expenseForm.amount || 0) + parseFloat(expenseForm.gst_amount || 0)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Type:</span>
                      <span className="font-medium">{expenseForm.expense_type.replace('_', ' ').toUpperCase()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Method:</span>
                      <span className="font-medium">{expenseForm.expense_method.replace('_', ' ').toUpperCase()}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700"
                >
                  {editingExpense ? 'Update Expense' : 'Record Expense'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Reports Modal */}
      {showReportsModal && (
        <Dialog open={showReportsModal} onOpenChange={setShowReportsModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-gray-800 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Export Expense Reports
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Generate expense reports with custom date filters
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={reportForm.start_date}
                    onChange={(e) => setReportForm({...reportForm, start_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={reportForm.end_date}
                    onChange={(e) => setReportForm({...reportForm, end_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Report Details:</h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <p>• Date range: {reportForm.start_date} to {reportForm.end_date}</p>
                  <p>• Columns: Date, Vendor, Type, Amount, GST, Method, Notes</p>
                  <p>• Format: Excel (.xlsx)</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowReportsModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleExportReport}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Expenses;
